import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Save, RotateCcw, Eye, EyeOff, ChevronDown, ChevronRight, Info, History, CheckCircle, XCircle, Phone, Filter, ChevronLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const TEMPLATE_ICONS = {
  confirmation: '1', signature: '2', vehicle_ready: '3', additional_work: '4',
  appointment_reminder: '5', send_quote: '6', send_invoice: '7', satisfaction: '8',
};

const TYPE_LABELS = {
  whatsapp_confirmation: 'Confirmation RDV',
  whatsapp_api_confirmation: 'Confirmation RDV',
  whatsapp_signature: 'Signature',
  whatsapp_api_signature: 'Signature',
  whatsapp_vehicle_ready: 'Véhicule prêt',
  whatsapp_api_vehicle_ready: 'Véhicule prêt',
  whatsapp_additional_work: 'Travaux supplémentaires',
  whatsapp_api_additional_work: 'Travaux supplémentaires',
  vehicle_ready: 'Véhicule prêt',
  appointment_reminder: 'Rappel RDV',
  send_quote: 'Envoi devis',
  send_invoice: 'Envoi facture',
  satisfaction_whatsapp: 'Satisfaction',
  satisfaction: 'Satisfaction',
};

function TemplateEditor({ template, onSave, onReset }) {
  const [content, setContent] = useState(template.content);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setContent(template.content); setDirty(false); }, [template]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(template.key, content); setDirty(false); toast.success('Template sauvegardé'); }
    catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const handleReset = async () => {
    try { const r = await onReset(template.key); setContent(r.content); setDirty(false); toast.success('Template réinitialisé'); }
    catch { toast.error('Erreur'); }
  };

  const handlePreview = async () => {
    if (showPreview) { setShowPreview(false); return; }
    try { const r = await axios.post(`${API}/message-templates/${template.key}/preview`, { content }); setPreview(r.data.preview); setShowPreview(true); }
    catch { toast.error('Erreur de prévisualisation'); }
  };

  const insertVariable = (varKey) => {
    const ta = document.getElementById(`template-${template.key}`);
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const nc = content.substring(0, s) + `{${varKey}}` + content.substring(e);
    setContent(nc); setDirty(true);
    setTimeout(() => { ta.focus(); const p = s + varKey.length + 2; ta.setSelectionRange(p, p); }, 0);
  };

  return (
    <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid={`template-card-${template.key}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        onClick={() => setExpanded(!expanded)} data-testid={`template-header-${template.key}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">{TEMPLATE_ICONS[template.key] || '?'}</div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white text-sm">{template.name}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">{template.description}</p>
          </div>
          {template.is_custom && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Personnalisé</span>}
          {dirty && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Non sauvegardé</span>}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </div>
      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500 dark:text-slate-400">Variables disponibles (cliquer pour insérer) :</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(template.variables || []).map((v) => (
                <button key={v.key} onClick={() => insertVariable(v.key)}
                  className="text-[11px] px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-900 dark:hover:text-emerald-300 transition-colors font-mono"
                  title={`${v.label} — ex: ${v.example}`} data-testid={`var-btn-${template.key}-${v.key}`}>
                  {`{${v.key}}`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 block">Contenu du message</label>
            <textarea id={`template-${template.key}`} value={content} onChange={(e) => { setContent(e.target.value); setDirty(true); }}
              rows={Math.min(20, Math.max(8, content.split('\n').length + 2))}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm p-3 font-mono leading-relaxed resize-y focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              data-testid={`template-textarea-${template.key}`} />
          </div>
          {showPreview && (
            <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4" data-testid={`template-preview-${template.key}`}>
              <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">Apercu avec données exemple</div>
              <pre className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">{preview}</pre>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving || !dirty} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`save-btn-${template.key}`}>
              <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
            <Button onClick={handlePreview} variant="outline" size="sm" data-testid={`preview-btn-${template.key}`}>
              {showPreview ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
              {showPreview ? 'Masquer' : 'Apercu'}
            </Button>
            {template.is_custom && (
              <Button onClick={handleReset} variant="ghost" size="sm" className="text-slate-500 hover:text-red-600" data-testid={`reset-btn-${template.key}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Réinitialiser
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function LogRow({ log }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isSuccess = log.status === 'sent';
  const isWA = log.channel?.includes('whatsapp');
  const label = TYPE_LABELS[log.type] || log.type;
  const date = log.created_at ? new Date(log.created_at) : null;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid={`log-row-${log.id}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        {isSuccess
          ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
          : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isWA ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
              {isWA ? 'WhatsApp' : 'Email'}
            </span>
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{label}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{log.recipient_name || '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {log.recipient_phone && (
            <span className="text-[11px] text-slate-400 font-mono hidden sm:inline">{log.recipient_phone}</span>
          )}
          <span className="text-[11px] text-slate-400">
            {date ? date.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
            {' '}
            {date ? date.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </div>
      {expanded && log.message && (
        <div className="border-t border-slate-100 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/50">
          <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">{log.message}</pre>
        </div>
      )}
    </div>
  );
}

function HistoryTab() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const PAGE_SIZE = 30;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (channelFilter !== 'all') params.set('channel', channelFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/notification-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  }, [page, channelFilter, statusFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const sentCount = logs.filter(l => l.status === 'sent').length;
  const errorCount = logs.filter(l => l.status === 'error').length;

  return (
    <div className="space-y-4" data-testid="history-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-slate-800 dark:text-white">{total}</p>
          <p className="text-xs text-slate-500">Total messages</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{sentCount}</p>
          <p className="text-xs text-slate-500">Envoyés (page)</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-red-500">{errorCount}</p>
          <p className="text-xs text-slate-500">Erreurs (page)</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={channelFilter} onValueChange={(v) => { setChannelFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="channel-filter">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les canaux</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="sent">Envoyé</SelectItem>
            <SelectItem value="error">Erreur</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Log list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Aucun message envoyé pour le moment</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => <LogRow key={log.id} log={log} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>
          <span className="text-xs text-slate-500">Page {page + 1} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
            Suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function WhatsappTemplatesPage({ auth, onLogout }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');

  const fetchTemplates = useCallback(async () => {
    try { const r = await axios.get(`${API}/message-templates`); setTemplates(r.data); }
    catch { toast.error('Erreur de chargement des templates'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async (key, content) => {
    const r = await axios.put(`${API}/message-templates/${key}`, { content });
    setTemplates(prev => prev.map(t => t.key === key ? { ...t, ...r.data } : t));
  };

  const handleReset = async (key) => {
    const r = await axios.post(`${API}/message-templates/${key}/reset`);
    setTemplates(prev => prev.map(t => t.key === key ? { ...t, ...r.data } : t));
    return r.data;
  };

  const manualTemplates = templates.filter(t => t.key !== 'satisfaction');
  const autoTemplates = templates.filter(t => t.key === 'satisfaction');

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="whatsapp-templates-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">Messages WhatsApp</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Gérez vos templates et consultez l'historique d'envoi</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-templates"
          >
            <MessageCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />Templates
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-history"
          >
            <History className="w-4 h-4 inline mr-1.5 -mt-0.5" />Historique
          </button>
        </div>

        {activeTab === 'templates' && (
          <>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4" data-testid="templates-info-banner">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Comment ca marche :</strong> Modifiez le texte de chaque message. Les variables entre accolades
                (ex: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded font-mono text-xs">{'{nom_client}'}</code>) seront
                automatiquement remplacées par les vraies données au moment de l'envoi.
              </p>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div>
                  <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">Messages manuels (envoyés au clic)</h2>
                  <div className="space-y-3">
                    {manualTemplates.map(t => <TemplateEditor key={t.key} template={t} onSave={handleSave} onReset={handleReset} />)}
                  </div>
                </div>
                {autoTemplates.length > 0 && (
                  <div>
                    <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">Messages automatiques</h2>
                    <div className="space-y-3">
                      {autoTemplates.map(t => <TemplateEditor key={t.key} template={t} onSave={handleSave} onReset={handleReset} />)}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'history' && <HistoryTab />}
      </div>
    </AdminLayout>
  );
}
